/**
 * The four management figures of the Kautionen tab.
 *
 * FOUR, NOT THE PAGE'S SIX
 * ------------------------
 * `/admin/kautionen` shows six because it has a six-wide row to put them in. This
 * tab lives in the company overview, whose KPI grid is 1 → 2 → 4 everywhere
 * (`OverviewKpiGrid`), so six would either need a foreign six-column row or leave
 * a ragged 4+2. Two of the page's six are also both titled "davon Versicherung",
 * which reads fine directly under its parent card and not at all in a flat row.
 *
 * WHAT IS DROPPED IS THE BREAKDOWN, NEVER THE MEANING
 * ---------------------------------------------------
 * The cash and bank splits move into the first card's footer rather than
 * disappearing, and the unresolved slice keeps its own amber line. A summary may
 * be shorter than the page it summarises; it may not be quieter about the parts
 * of a total that are not what they look like.
 *
 * The colours are the page's: teal for what tenants placed with us, violet for
 * what we placed with landlords, amber for the premium — so a reader arriving
 * from `/admin/kautionen` recognises the same two worlds.
 */
import React from "react";
import { Landmark, ShieldCheck, Tag } from "lucide-react";

import OverviewKpiCard from "../companyOverview/OverviewKpiCard";
import { chf, chfPrecise, percent } from "../companyFinance/financeFormat";

/** "59.1 % des Gesamtbetrags", or nothing when the share cannot be stated. */
function shareText(value) {
  const p = percent(value);
  return p ? `${p} des Gesamtbetrags` : null;
}

/** "4 Mietverhältnisse" / "1 Mietverhältnis", or nothing when the count is absent. */
function countText(value, one, many) {
  if (value === null || value === undefined) return null;
  return `${value} ${value === 1 ? one : many}`;
}

export default function DepositsTabKpis({
  tenant,
  landlord,
  unresolved = null,
  tenantLoading = false,
  landlordLoading = false,
  tenantError = "",
  landlordError = "",
  onRetryTenant,
  onRetryLandlord,
}) {
  return (
    <div
      data-testid="deposits-tab-kpis"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      <OverviewKpiCard
        testId="deposits-tab-kpi-tenant-total"
        title="Mieterkautionen gesamt"
        value={chf(tenant.total)}
        subtext={countText(tenant.tenancyCount, "Mietverhältnis", "Mietverhältnisse")}
        icon={<ShieldCheck className="h-4 w-4" />}
        accent="teal"
        compact
        loading={tenantLoading}
        error={tenantError}
        onRetry={onRetryTenant}
        footer={
          <div className="space-y-1">
            <span
              data-testid="deposits-tab-tenant-breakdown"
              className="block text-[11px] leading-snug text-muted-foreground"
            >
              {`Bar ${chf(tenant.cash)} · Versicherung ${chf(
                tenant.insurance
              )} · Bank ${chf(tenant.bank)}`}
            </span>
            {/* Part of the headline has no established type. It stays IN the total —
                the money is real — but a reader must be able to see that a slice of
                it is unclassified rather than find out later in a table. */}
            {unresolved ? (
              <span
                data-testid="deposits-tab-unresolved"
                className="block text-[11px] leading-snug text-amber-700 dark:text-amber-300"
              >
                {`${chf(unresolved.amount)} nicht zugeordnet`}
              </span>
            ) : null}
          </div>
        }
      />

      <OverviewKpiCard
        testId="deposits-tab-kpi-tenant-insurance"
        title="Versicherte Mieterkautionen"
        value={chf(tenant.insurance)}
        subtext={shareText(tenant.insuranceShare)}
        icon={<ShieldCheck className="h-4 w-4" />}
        accent="teal"
        compact
        loading={tenantLoading}
        error={tenantError}
        onRetry={onRetryTenant}
      />

      <OverviewKpiCard
        testId="deposits-tab-kpi-landlord-total"
        title="Sicherheitsleistungen bei Vermietern"
        value={chf(landlord.total)}
        subtext={countText(landlord.unitsWithDeposit, "Einheit", "Einheiten")}
        icon={<Landmark className="h-4 w-4" />}
        accent="violet"
        compact
        loading={landlordLoading}
        error={landlordError}
        onRetry={onRetryLandlord}
      />

      <OverviewKpiCard
        testId="deposits-tab-kpi-landlord-premium"
        title="Jahresprämien"
        /* Two decimals: a premium is an invoiced figure, and rounding it to the
           franc would make it look like the rounded balances beside it. */
        value={chfPrecise(landlord.annualPremium)}
        subtext="Versicherungsprämien p.a. · nicht Teil der Sicherheitsbeträge"
        icon={<Tag className="h-4 w-4" />}
        accent="amber"
        compact
        loading={landlordLoading}
        error={landlordError}
        onRetry={onRetryLandlord}
      />
    </div>
  );
}
